/**
 * A one-slot buffer for the route a notification tap asked for.
 *
 * A tap can fire before the app can act on it: on a cold start the native listener is
 * installed as `NativeShell` mounts, which is inside `<Router>` but outside
 * `<AuthGate>`, and the auth store is still hydrating. Navigating right then would be
 * overwritten the moment the gate resolves. So the route is parked here and replayed
 * by whoever is able to route, exactly once.
 *
 * Mirrors `lib/auth-token.ts`'s unauthorized pair: a window event rather than an
 * import, so the notification module never has to know about the router.
 */

/** Fired when a notification tap has parked a route the app has yet to open. */
export const PENDING_ROUTE_EVENT = "elyxeer:pending-route";

let pendingRoute: string | null = null;

/** Park a route and wake any consumer that is already listening. */
export function setPendingRoute(route: string): void {
  pendingRoute = route;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(PENDING_ROUTE_EVENT));
  }
}

/**
 * The parked route, consumed. Null when there is none.
 *
 * Taking rather than reading is what makes a replay safe: the consumer runs both on
 * mount and on the event, and a route must not be opened twice when both fire.
 */
export function takePendingRoute(): string | null {
  const route = pendingRoute;
  pendingRoute = null;
  return route;
}
